<template>
    <div>
        <NavbarOne/>

        <div class="flex items-center gap-4 flex-wrap bg-overlay p-14 sm:p-16 before:bg-title before:bg-opacity-70" :style="{backgroundImage:'url(' + bg + ')'}">
            <div class="text-center w-full">
                <h2 class="text-white text-8 md:text-[40px] font-normal leading-none text-center">My Requests</h2>
                <ul class="flex items-center justify-center gap-[10px] text-base md:text-lg leading-none font-normal text-white mt-3 md:mt-4">
                    <li><router-link to="/">Home</router-link></li>
                    <li>/</li>
                    <li class="text-primary">My Requests</li>
                </ul>
            </div>
        </div>

        <div class="s-py-100" data-aos="fade-up">
            <div class="container-fluid">
                <div class="max-w-[1720px] mx-auto flex items-start gap-8 md:gap-12 2xl:gap-24 flex-col md:flex-row my-profile-navtab">
                    <div class="w-full md:w-[200px] lg:w-[300px] flex-none">
                        <ProfileTab/>
                    </div>
                    <div class="w-full md:w-auto md:flex-1 overflow-auto">
                        <div class="bg-[#F8F8F9] dark:bg-dark-secondary p-5 sm:p-8 lg:p-[50px]">

                            <div v-if="loading" class="grid gap-5">
                                <div v-for="n in 2" :key="n" class="mr-skeleton"></div>
                            </div>

                            <div v-else-if="error" class="mr-empty">
                                <p>{{ error }}</p>
                                <button type="button" class="btn btn-outline mt-5" data-text="Try again" @click="load"><span>Try again</span></button>
                            </div>

                            <div v-else-if="!requests.length" class="mr-empty">
                                <h4 class="text-xl font-semibold mb-2">No quotation requests yet</h4>
                                <p class="text-paragraph dark:text-white-light">Tell us what you would like made and we will come back with a price and a build time.</p>
                                <router-link to="/request-quotation" class="btn btn-solid mt-6" data-text="Request a quotation"><span>Request a quotation</span></router-link>
                            </div>

                            <div v-else class="grid gap-5">
                                <article v-for="request in requests" :key="request.id" class="mr-card">
                                    <header class="mr-card__head">
                                        <div class="min-w-0">
                                            <div class="flex items-center gap-3 flex-wrap">
                                                <h4 class="mr-card__title">{{ request.title }}<span v-if="request.quantity > 1" class="mr-card__qty"> × {{ request.quantity }}</span></h4>
                                                <span class="mr-status" :class="`mr-status--${request.status}`">{{ request.status_label }}</span>
                                            </div>
                                            <p class="mr-card__meta">
                                                {{ request.reference }} · sent {{ formatDate(request.created_at) }}
                                                <span v-if="request.item_type_label"> · {{ request.item_type_label }}</span>
                                            </p>
                                        </div>
                                        <button type="button" class="mr-card__toggle" @click="toggle(request.id)">
                                            {{ isOpen(request.id) ? 'Hide details' : 'Details' }}
                                        </button>
                                    </header>

                                    <!-- Timeline -->
                                    <ol v-if="!request.is_closed" class="mr-timeline" aria-label="Progress">
                                        <li v-for="stepItem in request.timeline" :key="stepItem.key" :class="{ 'is-done': stepItem.done, 'is-current': stepItem.current }">
                                            <span class="mr-timeline__dot"></span>
                                            <span class="mr-timeline__label">{{ stepItem.label }}</span>
                                        </li>
                                    </ol>

                                    <!-- Quotations -->
                                    <div v-if="request.quotations.length" class="mr-quotes">
                                        <div v-for="quotation in request.quotations" :key="quotation.id" class="mr-quote">
                                            <div class="min-w-0">
                                                <div class="mr-quote__title">Invoice {{ quotation.number }}</div>
                                                <div class="mr-quote__meta">
                                                    {{ formatPrice(quotation.total, quotation.currency) }}
                                                    <span v-if="quotation.valid_until"> · valid until {{ formatDate(quotation.valid_until) }}</span>
                                                    <span v-if="quotation.customer_response === 'accepted'" class="mr-quote__ok"> · accepted</span>
                                                    <span v-else-if="quotation.customer_response === 'changes_requested'"> · you asked for changes</span>
                                                </div>
                                            </div>
                                            <a :href="quotation.url" target="_blank" rel="noopener" class="btn btn-sm" :class="quotation.is_open ? 'btn-solid' : 'btn-outline'" :data-text="quotation.is_open ? 'Review & accept' : 'View'">
                                                <span>{{ quotation.is_open ? 'Review & accept' : 'View' }}</span>
                                            </a>
                                        </div>
                                    </div>
                                    <p v-else-if="!request.is_closed" class="mr-waiting">
                                        We are working on your invoice. You will get an email as soon as it is ready.
                                    </p>

                                    <!-- Details -->
                                    <div v-if="isOpen(request.id)" class="mr-details">
                                        <p class="mr-details__desc">{{ request.description || 'No description.' }}</p>
                                        <dl class="mr-details__list">
                                            <div v-if="request.dimensions.width || request.dimensions.height || request.dimensions.depth">
                                                <dt>Size</dt>
                                                <dd>{{ [request.dimensions.width && `${request.dimensions.width} W`, request.dimensions.height && `${request.dimensions.height} H`, request.dimensions.depth && `${request.dimensions.depth} D`].filter(Boolean).join(' × ') }} {{ request.dimensions.unit }}</dd>
                                            </div>
                                            <div v-for="modifier in request.selected_modifiers" :key="modifier.modifier_id || modifier.modifier_name">
                                                <dt>{{ modifier.modifier_name }}</dt>
                                                <dd>{{ (modifier.options || []).map((option) => option.label).join(', ') }}</dd>
                                            </div>
                                            <div v-if="request.budget_min || request.budget_max">
                                                <dt>Budget</dt>
                                                <dd>{{ request.budget_min ? formatPrice(request.budget_min) : '' }}{{ request.budget_min && request.budget_max ? ' – ' : '' }}{{ request.budget_max ? formatPrice(request.budget_max) : '' }}</dd>
                                            </div>
                                            <div v-if="request.target_date"><dt>Needed by</dt><dd>{{ formatDate(request.target_date) }}</dd></div>
                                            <div v-if="request.product"><dt>Based on</dt><dd><router-link :to="`/product-details/${request.product.id}`" class="text-primary">{{ request.product.name }}</router-link></dd></div>
                                        </dl>
                                        <div v-if="request.reference_images.length" class="mr-details__images">
                                            <a v-for="image in request.reference_images" :key="image" :href="image" target="_blank" rel="noopener"><img :src="image" alt=""></a>
                                        </div>
                                    </div>
                                </article>

                                <div class="pt-2">
                                    <router-link to="/request-quotation" class="btn btn-outline" data-text="New request"><span>New request</span></router-link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <FooterOne/>
        <ScrollToTop/>
    </div>
</template>

<script setup>
    import { onMounted, ref } from 'vue';
    import NavbarOne from '@/components/navbar/navbar-one.vue';
    import ProfileTab from '@/components/profile-tab.vue';
    import FooterOne from '@/components/footer/footer-one.vue';
    import ScrollToTop from '@/components/scroll-to-top.vue';
    import bg from '@/assets/img/shortcode/breadcumb.jpg';
    import Aos from 'aos';
    import { apiRequest } from '@/lib/client-api';
    import { formatPrice } from '@/lib/storefront-api';
    import { useClientSession } from '@/composables/use-client-session';

    const session = useClientSession();
    const requests = ref([]);
    const loading = ref(true);
    const error = ref('');
    const open = ref(new Set());

    const isOpen = (id) => open.value.has(id);
    const toggle = (id) => {
        const next = new Set(open.value);
        next.has(id) ? next.delete(id) : next.add(id);
        open.value = next;
    };

    const formatDate = (value) => {
        if (!value) return '';
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
    };

    async function load() {
        loading.value = true;
        error.value = '';
        try {
            await session.initializeSession();
            const response = await apiRequest('/custom-requests', { token: session.token.value });
            requests.value = response.data || [];
        } catch (e) {
            error.value = e.message || 'We could not load your requests.';
        } finally {
            loading.value = false;
        }
    }

    onMounted(() => {
        Aos.init();
        load();
    });
</script>

<style scoped>
.mr-skeleton { height: 150px; border-radius: 16px; background: #eef0f2; animation: mr-pulse 1.2s ease-in-out infinite; }
@keyframes mr-pulse { 50% { opacity: 0.5; } }
.mr-empty { text-align: center; padding: 40px 16px; }

.mr-card { background: #fff; border: 1px solid rgba(23, 36, 48, 0.1); border-radius: 16px; padding: 20px 22px; }
.mr-card__head { display: flex; justify-content: space-between; gap: 14px; align-items: flex-start; }
.mr-card__title { font-size: 18px; font-weight: 600; color: #172430; line-height: 1.3; }
.mr-card__qty { color: #8a9197; font-weight: 400; }
.mr-card__meta { font-size: 13px; color: #8a9197; margin-top: 4px; }
.mr-card__toggle { font-size: 13px; color: #bb976d; text-decoration: underline; text-underline-offset: 2px; white-space: nowrap; }

.mr-status { font-size: 12px; font-weight: 600; padding: 4px 10px; border-radius: 999px; background: #f3f4f5; color: #3c474e; }
.mr-status--quoted { background: #fff4e5; color: #9a5b00; }
.mr-status--accepted, .mr-status--converted { background: #e6f6ea; color: #1f7a3a; }
.mr-status--rejected { background: #eef0f2; color: #8a9197; }

.mr-timeline { display: flex; gap: 0; margin: 18px 0 0; padding: 0; list-style: none; }
.mr-timeline li { flex: 1; position: relative; text-align: center; font-size: 11.5px; color: #8a9197; }
.mr-timeline li::before { content: ''; position: absolute; top: 6px; left: 0; right: 0; height: 2px; background: #e8e9ea; }
.mr-timeline li:first-child::before { left: 50%; }
.mr-timeline li:last-child::before { right: 50%; }
.mr-timeline li.is-done::before { background: #bb976d; }
.mr-timeline__dot { position: relative; z-index: 1; display: block; width: 14px; height: 14px; margin: 0 auto 6px; border-radius: 999px; background: #fff; border: 2px solid #e8e9ea; }
.mr-timeline li.is-done .mr-timeline__dot { background: #bb976d; border-color: #bb976d; }
.mr-timeline li.is-current .mr-timeline__dot { box-shadow: 0 0 0 4px rgba(187, 151, 109, 0.2); }
.mr-timeline li.is-current .mr-timeline__label { color: #172430; font-weight: 600; }
@media (max-width: 560px) { .mr-timeline__label { display: none; } .mr-timeline li.is-current .mr-timeline__label { display: block; } }

.mr-quotes { display: grid; gap: 8px; margin-top: 18px; }
.mr-quote { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 12px 14px; border-radius: 12px; background: #f8f5f0; flex-wrap: wrap; }
.mr-quote__title { font-weight: 600; color: #172430; }
.mr-quote__meta { font-size: 13px; color: #3c474e; }
.mr-quote__ok { color: #1f7a3a; font-weight: 600; }
.mr-waiting { margin-top: 16px; font-size: 14px; color: #3c474e; }

.mr-details { margin-top: 16px; padding-top: 16px; border-top: 1px solid #eceff1; }
.mr-details__desc { white-space: pre-line; font-size: 14px; color: #3c474e; line-height: 1.6; }
.mr-details__list { display: grid; gap: 6px; margin: 12px 0 0; }
.mr-details__list > div { display: grid; grid-template-columns: 130px minmax(0, 1fr); gap: 10px; font-size: 14px; }
.mr-details__list dt { color: #8a9197; }
.mr-details__list dd { margin: 0; color: #172430; }
.mr-details__images { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; }
.mr-details__images img { width: 72px; height: 72px; object-fit: cover; border-radius: 8px; border: 1px solid #e8e9ea; }
</style>

<style>
.dark .mr-card { background: #172430; border-color: #3c474e; }
.dark .mr-card__title, .dark .mr-quote__title, .dark .mr-details__list dd, .dark .mr-timeline li.is-current .mr-timeline__label { color: #fff; }
.dark .mr-quote { background: #1e2a35; }
.dark .mr-quote__meta, .dark .mr-waiting, .dark .mr-details__desc { color: #cfd4d8; }
.dark .mr-status { background: #22313d; color: #cfd4d8; }
.dark .mr-timeline__dot { background: #172430; }
.dark .mr-details { border-color: #3c474e; }
.dark .mr-skeleton { background: #22313d; }
</style>
